import startOfDay from 'date-fns/startOfDay'
import * as React from 'react'
import { useHistory } from 'react-router'
import * as CreditsActions from '../../../../actions/credits'
import { CreditCreateAction, CreditCreation } from '../../../../actions/credits/createCredit'
import { CreditUpdateAction } from '../../../../actions/credits/updateCredit'
import { useAppDispatch } from '../../../../actions/types'
import { Routes } from '../../../../constants'
import type { Credit } from '../../../../types'
import type { CreditFormData } from '../types'
import * as Validation from '../validation'
import useCreditParams from '../useCreditParams'
import useDisplayTypes from './useDisplayTypes'

export default function useCreditFormData(credit?: Credit) {
    const dispatch = useAppDispatch()
    const creditTypes = useDisplayTypes()
    const history = useHistory()
    const { estate } = useCreditParams()
    const [creditData, setCreditData] = React.useState<CreditFormData>({
        type: '',
        start_date: startOfDay(new Date()),
        end_date: startOfDay(new Date(Date.now() + 1000 * 60 * 60 * 24 * 7)),
        skus: [],
    })
    const [saveProgress, setSaveProgress] = React.useState<undefined | number>()
    const [areUnsavedChanges, setUnsavedChanges] = React.useState(false)
    const [formErrors, setFormErrors] = React.useState<Validation.CreditValidation>({})
    const [submitError, setSubmitErrors] = React.useState<Error>()

    React.useEffect(() => {
        const isNew = !credit

        if (isNew) {
            setCreditData(prevCredit => ({
                ...prevCredit,
                type: creditTypes.length > 0
                    ? creditTypes[0].value ?? ''
                    : '',
            }))
        } else {
            setCreditData(prevCredit => ({
                ...prevCredit,
                ...credit,
            }))
        }
    }, [credit, creditTypes])

    const isNew = !credit

    const thereAreErrors =
        (validation: Validation.CreditValidation) => Object.keys(validation).length > 0

    const createCredit = React.useCallback(async (creditData: CreditFormData) => {
        setSubmitErrors(undefined)
        setSaveProgress(0)

        const createCreditAction: CreditCreateAction = await dispatch(
            CreditsActions.createCredit(creditData as CreditCreation)
        )

        if (createCreditAction.type !== 'CREDIT_CREATE##SUCCESS') {
            setSubmitErrors(createCreditAction.payload)
            setSaveProgress(undefined)
            return
        }

        const { id: creditId } = createCreditAction.payload
        setSaveProgress(1)

        history.replace(
            Routes.buildRoute(
                Routes.SELF_SERVE_CREDITS,
                { estate, credit: creditId },
            )
        )
    }, [dispatch, estate, history])

    const updateCredit = React.useCallback(async (creditData: CreditFormData) => {
        setSubmitErrors(undefined)
        setSaveProgress(0)

        const updateCreditAction: CreditUpdateAction = await dispatch(
            CreditsActions.updateCredit(creditData as Credit)
        )

        if (updateCreditAction.type !== 'CREDIT_UPDATE##SUCCESS') {
            setSubmitErrors(updateCreditAction.payload)
            setSaveProgress(undefined)
            return
        }

        setSaveProgress(1)
    }, [dispatch])

    const onSubmit = React.useCallback(() => {
        setFormErrors({})
        setUnsavedChanges(false)

        const errors = isNew
            ? Validation.validateCreation(creditData)
            : Validation.validateUpdate(creditData)

        if (thereAreErrors(errors)) {
            setFormErrors(errors)
            return
        }

        const saveAction = isNew ? createCredit : updateCredit

        saveAction(creditData).then(
            () => setTimeout(() => setSaveProgress(undefined), 5000),
        )
    }, [createCredit, isNew, creditData, updateCredit])

    return {
        areUnsavedChanges,
        formErrors,
        onSubmit,
        creditData,
        saveProgress,
        setFormErrors,
        setSubmitErrors,
        setCreditData,
        setUnsavedChanges,
        submitError,
    }
}
